import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, RadioGroup, ResultTable } from '../_fitnessHelpers';

export default function BodyTypeCalc() {
  const [tab, setTab] = useState(0);
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState(25);
  // US Units
  const [hIn, setHIn] = useState(70);
  const [wristIn, setWristIn] = useState(7);
  const [ankleIn, setAnkleIn] = useState(9);
  const [wtLb, setWtLb] = useState(160);
  // Metric
  const [hCm, setHCm] = useState(178);
  const [wristCm, setWristCm] = useState(17.8);
  const [ankleCm, setAnkleCm] = useState(22.9);
  const [wtKg, setWtKg] = useState(73);
  const clear = () => { setGender('male'); setAge(25); setHIn(70); setWristIn(7); setAnkleIn(9); setWtLb(160); setHCm(178); setWristCm(17.8); setAnkleCm(22.9); setWtKg(73); };

  const c = useMemo(() => {
    const h = tab === 0 ? +hIn||0 : (+hCm||0)/2.54;
    const w = tab === 0 ? +wristIn||0 : (+wristCm||0)/2.54;
    const a = tab === 0 ? +ankleIn||0 : (+ankleCm||0)/2.54;
    const wt = tab === 0 ? +wtLb||0 : (+wtKg||0)*2.20462;
    const ratio = w > 0 ? h/w : 0;
    let type;
    if (gender === 'male') {
      if (ratio > 10.4) type = 'Ectomorph';
      else if (ratio >= 9.6) type = 'Mesomorph';
      else type = 'Endomorph';
    } else {
      if (ratio > 11.0) type = 'Ectomorph';
      else if (ratio >= 10.1) type = 'Mesomorph';
      else type = 'Endomorph';
    }
    const desc = {
      Ectomorph: 'Lean, long, difficulty building mass. High metabolism, small joints.',
      Mesomorph: 'Athletic, medium frame, gains muscle easily and is responsive to training.',
      Endomorph: 'Softer build, wider joints, gains fat and muscle more readily.',
    }[type];
    // Body frame size (McArdle) via wrist size
    let frame;
    if (gender === 'male') frame = ratio > 10.4 ? 'Small' : ratio >= 9.6 ? 'Medium' : 'Large';
    else frame = ratio > 11.0 ? 'Small' : ratio >= 10.1 ? 'Medium' : 'Large';
    // Ankle / wrist as additional secondary check
    const anklewrist = w > 0 ? a / w : 0;
    return { type, ratio, desc, frame, wtLb: wt, anklewrist };
  }, [tab, gender, age, hIn, wristIn, ankleIn, wtLb, hCm, wristCm, ankleCm, wtKg]);

  return (
    <CalcShell look="cnet" title="Body Type Calculator" subtitle="Determine your somatotype (Ectomorph / Mesomorph / Endomorph) and body-frame size from wrist, ankle, height, weight and age.">
      <CalcNetPanel slug="bt" activeTab={tab} onTabChange={setTab} onClear={clear} right={
        <div>
          <div className="text-lg text-slate-800">Your dominant body type</div>
          <div className="text-2xl font-bold text-[#2f9c3f]" data-testid="bt-type">{c.type}</div>
          <p className="text-sm text-slate-700 mt-2">{c.desc}</p>
          <ResultTable testid="bt-info-table" rows={[
            ['Height : Wrist ratio', c.ratio.toFixed(2)],
            ['Ankle : Wrist ratio', c.anklewrist.toFixed(2)],
            ['Body frame size', c.frame],
            ['Weight (lb)', c.wtLb.toFixed(1)],
          ]}/>
        </div>
      }>
        <Row label="Gender"><RadioGroup name="bt-g" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]}/></Row>
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="bt-age"/></Row>
        {tab === 0 ? <>
          <Row label="Height"><UnitInput value={hIn} onChange={setHIn} unit="inches" testid="bt-h"/></Row>
          <Row label="Wrist"><UnitInput value={wristIn} onChange={setWristIn} unit="inches" testid="bt-w"/></Row>
          <Row label="Ankle"><UnitInput value={ankleIn} onChange={setAnkleIn} unit="inches" testid="bt-ankle"/></Row>
          <Row label="Weight"><UnitInput value={wtLb} onChange={setWtLb} unit="pounds" testid="bt-wt"/></Row>
        </> : <>
          <Row label="Height"><UnitInput value={hCm} onChange={setHCm} unit="cm" testid="bt-hcm"/></Row>
          <Row label="Wrist"><UnitInput value={wristCm} onChange={setWristCm} unit="cm" testid="bt-wcm"/></Row>
          <Row label="Ankle"><UnitInput value={ankleCm} onChange={setAnkleCm} unit="cm" testid="bt-anklecm"/></Row>
          <Row label="Weight"><UnitInput value={wtKg} onChange={setWtKg} unit="kg" testid="bt-wtkg"/></Row>
        </>}
      </CalcNetPanel>
    </CalcShell>
  );
}
